import GameConfig from '../GameConfig';
import * as PIXI from 'pixi.js';
import State from './State';
import {Spawn, MapState, createGrid} from '../../common/map/MapState';
import {Loc} from '../../common/map/Location';
import {clamp, distance} from '../../utils';
import {wallRenderer} from 'src/Pixi/map/WallRenderer';
import {Countdown} from '../Countdown';
import {SelectSpawn} from '../actions/SelectSpawn';
import {pointerRenderer} from 'src/Pixi/PointerRenderer';
import {heldRenderer} from 'src/Pixi/map/HeldRenderer';
import {spawnRenderer} from 'src/Pixi/map/SpawnRenderer';

import Point = PIXI.Point;

interface SpawnSelection {
    spawn: Spawn;
    distance: number;
    index: number;
}

const selectDuration = 10;

class SelectState extends State {
    private tile: Point;
    private highlighted: SpawnSelection | null;
    private hasSelected: boolean;
    private countdown: Countdown;

    public start(): void {
        this.hasSelected = false;
        this.countdown = new Countdown(selectDuration, 'Select starting castle');
        this.countdown.start();
        pointerRenderer.setPointerType('pointer');
    }

    public tick(): void {
        this.countdown.tick();

        if (this.countdown.durationLeft() === 0 && !this.hasSelected) {
            this.selectHighlighted();
        }
    }

    public finish(): void {
        this.countdown.finish();
    }

    public onMouseMove(pos: Point): void {
        if (this.hasSelected) return;
        this.highlight(pos);
    }

    public onMouseClick(pos: Point): void {
        if (this.hasSelected) return;
        this.highlight(pos);
        this.selectHighlighted();
    }

    private highlight(point: Point): void {
        this.tile = this.tilePosition(point);
        const closest = this.closestSpawnTo(this.tile);
        if (this.shouldSelect(closest)) {
            this.renderWalls(closest.spawn);
            this.highlighted = closest;
        }
    }

    private selectHighlighted(): void {
        this.hasSelected = true;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        new SelectSpawn(this.game).byClient(this.highlighted!.index);
    }

    private tilePosition({x, y}: Loc): Point {
        const tileX = clamp(x / GameConfig.tileSize, 0, this.game.map.width - 0.01);
        const tileY = clamp(y / GameConfig.tileSize, 0, this.game.map.height - 0.01);
        return new PIXI.Point(tileX, tileY);
    }

    private renderWalls(spawn: Spawn): void {
        const map = new MapState();
        map.playerCount = this.game.map.playerCount;
        map.width = this.game.map.width;
        map.height = this.game.map.height;
        map.walls = createGrid(map.width, map.height, _loc => null);
        spawn.walls.forEach(
            loc => (map.walls[loc.y][loc.x] = {owner: this.game.index, damaged: false})
        );
        map.spawns = this.game.map.spawns.map(spawn => ({...spawn}));
        map.updateHeld();
        map.heldBy[spawn.y][spawn.x] = this.game.index;
        spawnRenderer.redraw(map);
        wallRenderer.setMap(map);
        heldRenderer.setMap(map);
        heldRenderer.redraw();
    }

    private shouldSelect(closest: SpawnSelection): boolean {
        if (!this.highlighted) return true;
        return (
            closest.spawn.x !== this.highlighted.spawn.x ||
            closest.spawn.y !== this.highlighted.spawn.y
        );
    }

    private closestSpawnTo(point: Point): SpawnSelection {
        const distances = this.possibleSpawns().map(spawn => ({
            spawn,
            distance: distance(point, new PIXI.Point(spawn.x, spawn.y)),
        }));
        distances.sort((a, b) => a.distance - b.distance);
        const closest = distances[0];
        return {
            spawn: closest.spawn,
            distance: closest.distance,
            index: this.game.map.spawns.indexOf(closest.spawn),
        };
    }

    private possibleSpawns(): Spawn[] {
        return this.game.mode.possibleSpawnsForPlayer(this.game.index);
    }
}

export default SelectState;



// WEBPACK FOOTER //
// ./src/game/states/SelectState.ts